import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { customRangeFormat, shortMonthAndDay } from "@similarweb/pro-logic-components";

import type { DatePickerPreset } from "app/@types/ISwSettings";
import { swSettings } from "app/common/services/swSettings";
import { DurationSelectorPresetOriented } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented";
import { IndicatorTypes } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/types";
import { translate } from "app/filters/i18nFilter";
import { FixedDurationsSelector } from "app/components/duration-selector/FixedDurationsSelector";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { BasicDurations } from "app/services/DurationService";

import type { EngineName } from "../KeywordGeneratorModuleFrame";

const presentNotificationTooltipKey = "website.analysis.present.duration.notification.tooltip";

interface IRawValue {
    from: Dayjs;
    to: Dayjs;
    isCustom: boolean;
    isWindow: boolean;
    isDaily: boolean;
}

type KeywordGeneratorDurationPresets = DatePickerPreset & { rawValue?: IRawValue };

const getDatePickerPresets = (
    startDate: Dayjs,
    endDate: Dayjs,
    // date to display in suffix
    endDateForSuffix?: Dayjs,
): KeywordGeneratorDurationPresets[] => {
    const startDateString = `${startDate.format(customRangeFormat)}`;
    const endDateString = `${endDate.format(customRangeFormat)}`;
    const displayTextSuffixForWindow = `(As of ${endDate.format(shortMonthAndDay)})`;
    const displayTextSuffixForAllTime = `(As of ${endDateForSuffix.format(shortMonthAndDay)})`;
    return [
        {
            buttonText: translate("datepicker.lastn", {
                count: "28",
                unit: translate("datepicker.units.day.nomerous"),
            }),
            displayText: `${translate("datepicker.lastn", {
                count: "28",
                unit: translate("datepicker.units.day.nomerous"),
            })} ${displayTextSuffixForWindow}`,
            value: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            enabled: true,
            tooltipContent: translate("datepicker.lastn.tooltip"),
        },
        {
            buttonText: translate("datepicker.allTime"),
            displayText: `${translate("datepicker.allTime")} ${displayTextSuffixForAllTime}`,
            value: `${startDateString}-${endDateString}`,
            enabled: true,
            rawValue: {
                from: startDate,
                to: endDate,
                isCustom: true,
                isWindow: false,
                isDaily: false,
            },
            tooltipContent: translate("datepicker.allTime.tooltip"),
        },
    ];
};

const preparePresets = (availableDurations: KeywordGeneratorDurationPresets[]) => {
    return availableDurations.map((duration) => {
        return {
            id: duration.value,
            text: duration.buttonText,
            disabled: !duration.enabled,
            locked: duration.locked,
            tooltipContent: duration.tooltipContent,
            value: duration.rawValue ?? DurationService.getDurationData(duration.value).raw,
            displayText: duration.displayText,
        };
    });
};

export const KeywordGeneratorToolDurationFilter: FC = () => {
    const { params } = useCurrentStateAndParams();
    const { keyword, duration, isDurationChanged, searchEngine } = params;
    const componentName = swSettings.current.componentId;

    const minDate = swSettings.current.startDate;
    const maxDate = swSettings.current.endDate;
    const maxWindowDate = swSettings.current.windowEndDate;
    const maxSnapshotDate = dayjs(swSettings.components.DataSettings.resources.LastAvailableSnapshotData).endOf(
        "month",
    );
    const presentNotificationForDurationChanged = isDurationChanged === true;
    const datePickerPresets = getDatePickerPresets(minDate, maxWindowDate, maxSnapshotDate);

    const onDurationChange = (duration) => {
        proNavigator.updateParams({
            duration,
            isDurationChanged: null,
        });
    };

    const ComponentClass = FixedDurationsSelector;

    if ((searchEngine as EngineName) !== "google") {
        return;
    }

    return !swSettings.user.isShortMonthIntervalsUser ? (
        <ComponentClass
            key="filter-duration"
            isDisabled={false}
            minDate={minDate}
            maxDate={maxDate}
            componentName={componentName}
            onSubmit={onDurationChange}
            presets={preparePresets(datePickerPresets)}
            initialPreset={duration}
            keys={keyword}
            disableCalendar={true}
            disableFooter={true}
            compareAllowed={false}
            hasPermissionsLock={swSettings.current.durationPaymentHook}
            presentNotificationIndicator={presentNotificationForDurationChanged}
            presentNotificationTooltipKey={presentNotificationTooltipKey}
            fullWidthPresetItems={true}
            appendTo="body"
        />
    ) : (
        <DurationSelectorPresetOriented
            minDate={minDate}
            maxDate={maxDate}
            isDisabled={false}
            onSubmit={onDurationChange}
            presets={preparePresets(datePickerPresets)}
            initialPreset={duration}
            disableCalendar={true}
            presentNotificationIndicator={presentNotificationForDurationChanged}
            presentNotificationTooltipKey={presentNotificationTooltipKey}
            indicatorType={IndicatorTypes.DOT}
        />
    );
};
